import React, { useEffect, useState } from "react";
import { adminApi } from "../../utils/adminApi";
import { paths } from "../../paths";
import { getCache, setCache, isFresh, invalidate } from "./adminCache";
import {
  AdminShell,
  Panel,
  ResultModal,
  StatCard,
  tierFor,
  TIER_CLS,
  fmtPts,
  Loading,
} from "./adminUi";

const rankBadge = (rank) => {
  if (rank === 1) return "bg-amber-100 text-amber-700";
  if (rank === 2) return "bg-gray-200 text-gray-600";
  if (rank === 3) return "bg-orange-100 text-orange-700";
  return "bg-black/[0.04] text-black/50";
};

const ManageLeaderboard = () => {
  const [users, setUsers] = useState(() => getCache("leaderboard") || []);
  const [loading, setLoading] = useState(() => !getCache("leaderboard"));
  const [editing, setEditing] = useState(null);
  const [points, setPoints] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // popup after a points adjustment
  const [pool, setPool] = useState(0); // total points available across live tasks (tier denominator)

  const refresh = () => {
    adminApi
      .get("/leaderboard")
      .then((res) => { setUsers(res.data || []); setCache("leaderboard", res.data || []); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isFresh("leaderboard")) { setLoading(false); return; }
    refresh();
  }, []);

  // Tier badges are a share of the live task-points pool — load that denominator.
  useEffect(() => {
    adminApi
      .get("/stats")
      .then((res) => setPool(res.data?.totalAvailablePoints || 0))
      .catch(() => {});
  }, []);

  const savePoints = async (user) => {
    // Number("") === 0, so a cleared field would silently zero the CA's score.
    // Treat an empty/invalid input as a no-op rather than a real 0.
    const next = points === "" ? NaN : Number(points);
    if (Number.isNaN(next) || next < 0) {
      alert("Enter a valid points value (0 or greater).");
      return;
    }
    const prev = user.points || 0;
    setBusy(true);
    try {
      await adminApi.patch(`/cas/${user.id}/points`, { points: next });
      setEditing(null);
      invalidate("cas", "dashboard");
      refresh();
      // New rank = how many CAs now sit strictly above this CA's adjusted total.
      const newRank = users.filter((u) => u.id !== user.id && (u.points || 0) > next).length + 1;
      setResult({ name: user.name, prev, next, newRank });
    } catch (err) {
      alert(err.response?.data?.message || "Could not update points");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loading label="Loading leaderboard..." />;

  const totalPoints = users.reduce((s, u) => s + (u.points || 0), 0);
  const topName = users[0]?.name || "—";

  return (
    <AdminShell
      tab={paths.adminLeaderboard}
      title="Leaderboard"
      subtitle="Live ranking of Campus Ambassadors by points earned."
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pl-2">
        <StatCard label="Ranked CA's" value={users.length} sub="on the board" />
        <StatCard
          label="Top CA"
          value={topName}
          valueClassName="text-2xl md:text-3xl"
          sub={`${fmtPts(users[0]?.points || 0)} pts`}
        />
        <StatCard label="Pts Awarded" value={fmtPts(totalPoints)} sub="across all CA's" />
      </div>

      <Panel title="Leaderboard" sub={`${users.length} ranked ambassadors`}>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-left border-collapse">
            <thead className="sticky top-0 bg-[#F5EFEB] z-10">
              <tr className="text-[10px] font-extrabold tracking-wider text-[#2B1B17]/40 uppercase">
                <th className="py-2 px-3 w-16">Rank</th>
                <th className="py-2 px-3">Name</th>
                <th className="py-2 px-3">College</th>
                <th className="py-2 px-3">Tier</th>
                <th className="py-2 px-3 text-right">Points</th>
                <th className="py-2 px-3 text-right"></th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {users.length === 0 && (
                <tr><td colSpan={6} className="text-center text-black/40 py-6">No ambassadors yet.</td></tr>
              )}
              {users.map((u, i) => {
                const tier = tierFor(u.points || 0, pool);
                return (
                  <tr key={u.id} className="border-b border-black/[0.03] last:border-none">
                    <td className="py-2.5 px-3">
                      <span className={`inline-flex items-center justify-center min-w-[2rem] text-xs font-black px-2 py-1 rounded-md ${rankBadge(i + 1)}`}>#{i + 1}</span>
                    </td>
                    <td className="py-2.5 px-3 font-bold text-[#2B1B17] whitespace-nowrap">{u.name}</td>
                    <td className="py-2.5 px-3 text-black/50 whitespace-nowrap">{u.college}</td>
                    <td className="py-2.5 px-3">
                      <span className={`text-[9px] font-extrabold tracking-wider px-2 py-1 rounded-md ${TIER_CLS[tier]}`}>{tier}</span>
                    </td>
                    <td className="py-2.5 px-3 text-right font-black text-[#2B1B17]">
                      {editing === u.id ? (
                        <input
                          type="number"
                          value={points}
                          onChange={(e) => setPoints(e.target.value)}
                          className="w-24 bg-white border border-black/10 rounded-lg px-3 py-1.5 text-sm text-right focus:outline-none focus:border-[#D15B28]"
                        />
                      ) : (
                        (u.points || 0).toLocaleString()
                      )}
                    </td>
                    <td className="py-2.5 px-3 text-right whitespace-nowrap">
                      {editing === u.id ? (
                        <span className="inline-flex items-center gap-2">
                          <button onClick={() => savePoints(u)} disabled={busy} className="text-xs font-bold text-teal-600 hover:opacity-80">Save</button>
                          <button onClick={() => setEditing(null)} className="text-xs font-bold text-black/40 hover:opacity-80">Cancel</button>
                        </span>
                      ) : (
                        <button
                          onClick={() => { setEditing(u.id); setPoints(u.points || 0); }}
                          className="text-xs font-bold text-[#D15B28] hover:opacity-80"
                        >
                          Edit Points →
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      {result && (() => {
        const delta = result.next - result.prev;
        const sign = delta >= 0 ? "+" : "";
        return (
          <ResultModal
            accent="peach"
            title="Points Updated!"
            message={`${result.name}'s total adjusted from ${result.prev.toLocaleString()} → ${result.next.toLocaleString()} pts. Leaderboard refreshed.`}
            chip={`${sign}${delta.toLocaleString()} pts · Rank updated to #${result.newRank}`}
            primary={{ label: "View Leaderboard", onClick: () => setResult(null) }}
            secondary={{ label: "Adjust Another", onClick: () => setResult(null) }}
            onClose={() => setResult(null)}
          />
        );
      })()}
    </AdminShell>
  );
};

export default ManageLeaderboard;
